import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Building2, Users, Plus, Trash2, RefreshCw,
  ChevronRight, Briefcase, Crown, Eye, Wrench, UserPlus,
  Mail, AlertTriangle, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const ROLE_CONFIG = {
  owner:     { label: 'Owner',     icon: Crown,     color: 'text-amber-400',   bg: 'bg-amber-500/15' },
  admin:     { label: 'Admin',     icon: Shield,    color: 'text-blue-400',    bg: 'bg-blue-500/15' },
  dept_lead: { label: 'Dept Lead', icon: Briefcase, color: 'text-purple-400',  bg: 'bg-purple-500/15' },
  operator:  { label: 'Operator',  icon: Wrench,    color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  viewer:    { label: 'Viewer',    icon: Eye,       color: 'text-slate-400',   bg: 'bg-slate-500/15' },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color} ${cfg.bg}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CreateOrgDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !ownerEmail.trim()) return;
    setLoading(true);
    try {
      await api.createOrg({
        name: name.trim(),
        domain: domain.trim() || null,
        owner_email: ownerEmail.trim(),
      });
      toast.success(`Created ${name}`);
      onCreated();
      onOpenChange(false);
      setName(''); setDomain(''); setOwnerEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create org');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Create Organization</DialogTitle>
          <DialogDescription className="text-slate-400">
            The owner email becomes the first admin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <label className="text-xs text-slate-500">Organization Name *</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corp" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Owner Email *</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} placeholder="owner@acme.com" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Domain (optional)</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={domain} onChange={e => setDomain(e.target.value)} placeholder="acme.com" />
          </div>
          <Button type="submit" disabled={loading || !name.trim() || !ownerEmail.trim()} className="w-full bg-blue-600 hover:bg-blue-700">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Organization
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InviteMemberDialog({ open, onOpenChange, orgId, departments, onInvited }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [deptId, setDeptId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.inviteMember(orgId, {
        email: email.trim(),
        role,
        department_id: deptId || null,
      });
      toast.success(`Invited ${email}`);
      onInvited();
      onOpenChange(false);
      setEmail(''); setRole('viewer'); setDeptId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Invite Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <label className="text-xs text-slate-500">Email *</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={email} onChange={e => setEmail(e.target.value)} placeholder="alice@company.com" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {Object.entries(ROLE_CONFIG).filter(([k]) => k !== 'owner').map(([k, v]) => (
                  <SelectItem key={k} value={k} className="text-slate-200 focus:bg-slate-700 focus:text-white">{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {departments.length > 0 && role === 'dept_lead' && (
            <div>
              <label className="text-xs text-slate-500">Department</label>
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {departments.map(d => (
                    <SelectItem key={d.department_id} value={d.department_id} className="text-slate-200 focus:bg-slate-700 focus:text-white">{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={loading || !email.trim()} className="w-full bg-blue-600 hover:bg-blue-700">
            <UserPlus className="w-4 h-4 mr-2" />
            Send Invitation
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateDepartmentDialog({ open, onOpenChange, orgId, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.createDepartment(orgId, { name: name.trim(), description });
      toast.success(`Created ${name} department`);
      onCreated();
      onOpenChange(false);
      setName(''); setDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create department');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Create Department</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <label className="text-xs text-slate-500">Name *</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="Engineering" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Description</label>
            <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={description} onChange={e => setDescription(e.target.value)} placeholder="Eng + DevOps + SRE" />
          </div>
          <Button type="submit" disabled={loading || !name.trim()} className="w-full bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Department
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrgDetail({ orgId, onBack }) {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);

  const fetchOrg = useCallback(async () => {
    try {
      const data = await api.getOrg(orgId);
      setOrg(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load org');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleRemoveMember = async (membershipId) => {
    try {
      await api.removeMember(orgId, membershipId);
      toast.success('Member removed');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleRoleChange = async (membershipId, role) => {
    try {
      await api.updateMemberRole(orgId, membershipId, role);
      toast.success('Role updated');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  if (loading) return <div className="text-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-blue-400 mx-auto" /></div>;
  if (!org) return <div className="text-center py-20 text-red-400">Organization not found</div>;

  return (
    <div>
      <Button variant="outline" className="mb-6 border-slate-600 text-slate-300" onClick={onBack}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        All organizations
      </Button>

      <div className="flex items-start justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">{org.name}</h2>
          <p className="text-slate-400 text-sm">
            {org.domain && <span className="font-mono mr-3">{org.domain}</span>}
            Owner: <span className="text-slate-300">{org.owner_email}</span>
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-white">{org.agent_count}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Agents</div>
        </div>
      </div>

      {/* Departments */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-purple-400" />
            Departments ({org.departments.length})
          </h3>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setDeptOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            New
          </Button>
        </div>
        {org.departments.length === 0 ? (
          <p className="text-sm text-slate-500">No departments yet. Create one to scope agent access.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {org.departments.map(d => (
              <div key={d.department_id} className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-white font-medium text-sm">{d.name}</p>
                {d.description && <p className="text-slate-500 text-xs mt-0.5">{d.description}</p>}
                <p className="text-slate-600 text-xs mt-2 font-mono">{d.department_id}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-400" />
            Members ({org.members.length})
          </h3>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setInviteOpen(true)}>
            <UserPlus className="w-3.5 h-3.5 mr-1" />
            Invite
          </Button>
        </div>
        <div className="space-y-2">
          {org.members.map(m => (
            <div key={m.membership_id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-white text-sm truncate">{m.email}</p>
                  <p className="text-slate-600 text-xs font-mono truncate">{m.membership_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {m.role !== 'owner' && (
                  <Select value={m.role} onValueChange={(r) => handleRoleChange(m.membership_id, r)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 w-32 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(ROLE_CONFIG).filter(([k]) => k !== 'owner').map(([k, v]) => (
                        <SelectItem key={k} value={k} className="text-slate-200 focus:bg-slate-700 text-xs">{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {m.role === 'owner' && <RoleBadge role="owner" />}
                {m.role !== 'owner' && (
                  <button
                    className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400"
                    onClick={() => handleRemoveMember(m.membership_id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Agents shortcut */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-blue-300 text-sm font-medium">Manage agents in this organization</p>
          <p className="text-blue-400/70 text-xs mt-0.5">View {org.agent_count} agent{org.agent_count === 1 ? '' : 's'} scoped to this org</p>
        </div>
        <Link to={`/agents?org_id=${orgId}`}>
          <Button variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/20">
            Open Agents
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        orgId={orgId}
        departments={org.departments}
        onInvited={fetchOrg}
      />
      <CreateDepartmentDialog
        open={deptOpen}
        onOpenChange={setDeptOpen}
        orgId={orgId}
        onCreated={fetchOrg}
      />
    </div>
  );
}

export default function OrgsPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const fetchOrgs = useCallback(async () => {
    try {
      const data = await api.listOrgs();
      setOrgs(data.orgs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white text-sm">Site Scan</Link>
            <Link to="/agents" className="text-slate-400 hover:text-white text-sm">Agent Control Plane</Link>
            <span className="text-white font-medium text-sm">Organizations</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {selectedOrgId ? (
          <OrgDetail orgId={selectedOrgId} onBack={() => { setSelectedOrgId(null); fetchOrgs(); }} />
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Building2 className="w-6 h-6 text-blue-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white">Organizations</h1>
                </div>
                <p className="text-slate-400 text-sm">
                  Manage multi-department agent ownership with RBAC scoped by role.
                </p>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Organization
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-20"><RefreshCw className="w-8 h-8 animate-spin text-blue-400 mx-auto" /></div>
            ) : orgs.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-slate-700 rounded-xl">
                <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 mb-2">No organizations yet</p>
                <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                  Create an organization to assign agents to departments and control who can manage them.
                </p>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Organization
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {orgs.map(org => (
                  <button
                    key={org.org_id}
                    onClick={() => setSelectedOrgId(org.org_id)}
                    className="text-left bg-slate-800/30 border border-slate-700/50 hover:border-blue-500/50 hover:bg-slate-800/60 rounded-xl p-5 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Building2 className="w-5 h-5 text-blue-400" />
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                    <p className="text-white font-semibold mb-1">{org.name}</p>
                    {org.domain && <p className="text-xs text-slate-500 font-mono mb-2">{org.domain}</p>}
                    <p className="text-xs text-slate-600">Owner: {org.owner_email}</p>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <CreateOrgDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={fetchOrgs}
        />
      </div>
    </div>
  );
}
